import moment from 'moment-timezone';

import { getTariffsTOUCosts } from '../api/Optimise';
import { ExtractedRates, TariffRates } from '../types/tariff';
import { processTariffRates } from '../ui/Form/TariffFormHelpers';

// Use Europe/London timezone for UK-based users
const USER_TIMEZONE = 'Europe/London';

/**
 * Converts TariffRates format to ExtractedRates format
 *
 * TariffRates uses {day, cheap, peak} with {min, max}
 * ExtractedRates uses {import/export} with rate categories having {minRate, maxRate}
 *
 * @param importRates - Import tariff rates in TariffRates format
 * @param exportRates - Export tariff rates in TariffRates format
 * @returns ExtractedRates format compatible with TariffRateTable component
 *
 * @example
 * const extractedRates = convertToExtractedRates(importRates, exportRates);
 * // Returns: { import: { 'Cheap rate': {...}, 'Day rate': {...}, 'Peak rate': {...} }, export: {...} }
 */
export const convertToExtractedRates = (
  importRates: TariffRates | null,
  exportRates: TariffRates | null
): ExtractedRates => {
  const result: ExtractedRates = {
    import: {
      'Cheap rate': { rates: [], minRate: null, maxRate: null },
      'Day rate': { rates: [], minRate: null, maxRate: null },
      'Peak rate': { rates: [], minRate: null, maxRate: null },
    },
    export: {
      'Cheap rate': { rates: [], minRate: null, maxRate: null },
      'Day rate': { rates: [], minRate: null, maxRate: null },
      'Peak rate': { rates: [], minRate: null, maxRate: null },
    },
  };

  // Map import rates
  if (importRates && result.import) {
    result.import['Cheap rate'].minRate = importRates.cheap.min;
    result.import['Cheap rate'].maxRate = importRates.cheap.max;
    result.import['Day rate'].minRate = importRates.day.min;
    result.import['Day rate'].maxRate = importRates.day.max;
    result.import['Peak rate'].minRate = importRates.peak.min;
    result.import['Peak rate'].maxRate = importRates.peak.max;
  }

  // Map export rates
  if (exportRates && result.export) {
    result.export['Cheap rate'].minRate = exportRates.cheap.min;
    result.export['Cheap rate'].maxRate = exportRates.cheap.max;
    result.export['Day rate'].minRate = exportRates.day.min;
    result.export['Day rate'].maxRate = exportRates.day.max;
    result.export['Peak rate'].minRate = exportRates.peak.min;
    result.export['Peak rate'].maxRate = exportRates.peak.max;
  }

  return result;
};

/**
 * Processes Time-of-Use (TOU) cost data to extract rate ranges by category
 *
 * This function fetches TOU cost data over a specified period (default 12 months),
 * processes it through rate categorization logic, and returns rate ranges for
 * each category (Cheap, Day, Peak) for both import and export tariffs.
 *
 * The function:
 * 1. Fetches TOU cost data for the specified time period
 * 2. Processes the data using the same categorization logic as the Savings page
 * 3. Calculates min/max ranges for each rate category
 * 4. Returns data in ExtractedRates format compatible with TariffRateTable
 *
 * @param importTariffId - The import tariff ID to fetch rates for
 * @param exportTariffId - The export tariff ID to fetch rates for
 * @param daysOfHistory - Number of days of historical data to fetch (default: 365 for 12 months)
 * @returns Promise resolving to ExtractedRates with min/max for each category
 *
 * @example
 * const rates = await processTOUCostsToRates('import-tariff-123', 'export-tariff-456');
 * // Returns: {
 * //   import: { 'Cheap rate': {minRate: 5.2, maxRate: 7.1}, ... },
 * //   export: { 'Cheap rate': {minRate: 3.0, maxRate: 4.5}, ... }
 * // }
 */
export const processTOUCostsToRates = async (
  importTariffId: string,
  exportTariffId: string,
  daysOfHistory: number = 365
): Promise<ExtractedRates> => {
  // Calculate the time range for fetching tariff data
  // Fetch data for the specified period (default 12 months) to capture rate variability
  const end = moment().tz(USER_TIMEZONE).startOf('day');
  const endTimestampUTC = end.unix();
  const start = end.clone().subtract(daysOfHistory, 'days');
  const startTimestampUTC = start.unix();

  try {
    // Fetch TOU costs for both import and export tariffs in parallel
    const [importTouCosts, exportTouCosts] = await Promise.all([
      getTariffsTOUCosts(startTimestampUTC, endTimestampUTC, importTariffId),
      getTariffsTOUCosts(startTimestampUTC, endTimestampUTC, exportTariffId),
    ]);

    // Process the cost windows to extract rate ranges by category
    const importRates = processTariffRates(importTouCosts.cost_windows, 'import');
    const exportRates = processTariffRates(exportTouCosts.cost_windows, 'export');

    // Convert to the format expected by TariffRateTable
    const result = convertToExtractedRates(importRates, exportRates);
    return result;
  } catch (error) {
    console.error('Error processing TOU costs to rates:', error);
    // Return empty structure on error
    return {
      import: {
        'Cheap rate': { rates: [], minRate: null, maxRate: null },
        'Day rate': { rates: [], minRate: null, maxRate: null },
        'Peak rate': { rates: [], minRate: null, maxRate: null },
      },
      export: {
        'Cheap rate': { rates: [], minRate: null, maxRate: null },
        'Day rate': { rates: [], minRate: null, maxRate: null },
        'Peak rate': { rates: [], minRate: null, maxRate: null },
      },
    };
  }
};
